<script setup lang="ts">
import { Star, Check, CheckCircle } from 'lucide-vue-next'
import { router } from '@inertiajs/vue3'
import { computed } from 'vue'
import { trackEvent } from '@/composables/useAnalytics'

interface Subscription {
  teacher_id: number
  tier: string
  expires_in: number | null
}

interface Props {
  tutorSlug: string
  tutorId?: number
  tutorName: string
  basePrice: number
  silverPrice: number
  goldPrice: number
  videosCount: number
  servicesOffered: string[]
  groupSubjects?: Array<{ id: number; name: string; levels: Array<{ id: number; name: string }> }>
  subscriptions?: Subscription[]
}

const props = withDefaults(defineProps<Props>(), {
  groupSubjects: () => [],
  subscriptions: () => [],
})

// Build descriptive text for group session subjects/levels
function groupSubjectText(): string {
  const gs = props.groupSubjects
  if (!gs || gs.length === 0) return 'Choose your subject'

  if (gs.length === 1) {
    const subject = gs[0]
    if (subject.levels.length === 0) return `Choose ${subject.name}`
    if (subject.levels.length === 1) return `Choose ${subject.name} ${subject.levels[0].name}`
    if (subject.levels.length === 2) return `Choose ${subject.name} ${subject.levels.map((l) => l.name).join(' or ')}`
    const first = subject.levels.slice(0, 2).map((l) => l.name).join(', ')
    return `Choose from ${subject.name} ${first} & more`
  }

  return `Choose ${gs.map((s) => s.name).join(' or ')}`
}

const offersVideos = props.servicesOffered.includes('videos') || props.servicesOffered.includes('materials')
const offersGroupSessions = props.servicesOffered.includes('group_sessions')
const offersOneOnOne = props.servicesOffered.includes('one_on_one')

// Check if plans are actually available (offered AND priced)
const contentLibraryAvailable = offersVideos && props.basePrice > 0
const groupTuitionAvailable = offersGroupSessions && props.silverPrice > 0
const oneOnOneAvailable = offersOneOnOne && props.goldPrice > 0

const activeSub = computed(() =>
  props.subscriptions.find((s) => s.teacher_id === props.tutorId)
)

const isSubscribed = computed(() => !!activeSub.value)

function isSubscribedToTier(tier: string): boolean {
  return activeSub.value?.tier === tier
}

function handleSubscribe(tier: 'base' | 'silver' | 'gold') {
  if (isSubscribed.value) return
  trackEvent('initiateCheckout', {
    tutor_name: props.tutorName,
    tutor_id: props.tutorId,
    tier,
  })
  router.visit(`/payment?tutor=${props.tutorSlug}&tier=${tier}`)
}

function scrollToPlans() {
  // Find the membership tab and click it, or scroll to the pricing section
  const membershipTab = document.querySelector('[value="membership"]') as HTMLElement
  if (membershipTab) {
    membershipTab.click()
    setTimeout(() => {
      membershipTab.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }
}
</script>

<template>
  <section class="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl shadow-sm p-6 sticky top-24">
    <div class="text-center mb-6">
      <div class="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
        <Star class="w-8 h-8 text-white fill-white" />
      </div>
      <h3 class="text-2xl font-bold text-gray-900 mb-2">Membership Plans</h3>
      <p class="text-gray-600 text-sm">Choose the perfect plan for you</p>
    </div>

    <div class="space-y-4 mb-6">
      <!-- Content Library Plan -->
      <div
        v-if="offersVideos"
        @click="contentLibraryAvailable && !isSubscribed && handleSubscribe('base')"
        :class="[
          'rounded-xl p-4 border-2 transition-all',
          isSubscribedToTier('base')
            ? 'bg-green-50 border-green-400 cursor-default'
            : isSubscribed
              ? 'bg-gray-100 border-gray-200 opacity-60 cursor-default'
              : contentLibraryAvailable
                ? 'bg-white border-gray-200 hover:border-gray-400 cursor-pointer'
                : 'bg-gray-200 border-gray-300 opacity-60 cursor-not-allowed',
        ]"
      >
        <div v-if="isSubscribedToTier('base')" class="flex items-center gap-1.5 text-green-700 text-[10px] font-bold mb-2">
          <CheckCircle class="w-3.5 h-3.5" />
          SUBSCRIBED
        </div>
        <div class="flex items-center justify-between mb-2">
          <h4 :class="['font-bold', contentLibraryAvailable ? 'text-gray-900' : 'text-gray-600']">Content Library</h4>
          <div v-if="contentLibraryAvailable" class="text-xl font-bold text-gray-600">
            £{{ Number(basePrice).toFixed(2) }}<span class="text-xs text-gray-600 font-normal">/week</span>
          </div>
          <div v-else class="text-xl font-bold text-gray-500">N/A</div>
        </div>
        <p :class="['text-xs mb-3', contentLibraryAvailable ? 'text-gray-600' : 'text-gray-500']">
          {{ videosCount }}+ videos & materials
        </p>
        <div :class="['flex items-center text-xs mb-1', contentLibraryAvailable ? 'text-gray-700' : 'text-gray-500']">
          <Check :class="['w-4 h-4 mr-2', contentLibraryAvailable ? 'text-gray-600' : 'text-gray-400']" />
          <span>Self-paced learning</span>
        </div>
      </div>

      <!-- Group Tuition Plan -->
      <div
        v-if="offersGroupSessions"
        @click="groupTuitionAvailable && !isSubscribed && handleSubscribe('silver')"
        :class="[
          'rounded-xl p-4 border-2 transition-all relative',
          isSubscribedToTier('silver')
            ? 'bg-green-50 border-green-400 cursor-default'
            : isSubscribed
              ? 'bg-gray-100 border-gray-200 opacity-60 cursor-default'
              : groupTuitionAvailable
                ? 'bg-white border-blue-400 hover:border-blue-600 cursor-pointer'
                : 'bg-gray-200 border-gray-300 opacity-60 cursor-not-allowed',
        ]"
      >
        <div v-if="isSubscribedToTier('silver')" class="absolute -top-2 right-2">
          <span class="bg-green-600 text-white px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1">
            <CheckCircle class="w-3 h-3" />
            SUBSCRIBED
          </span>
        </div>
        <div v-else-if="groupTuitionAvailable" class="absolute -top-2 right-2">
          <span class="bg-blue-600 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">POPULAR</span>
        </div>
        <div class="flex items-center justify-between mb-2">
          <h4 :class="['font-bold', groupTuitionAvailable ? 'text-gray-900' : 'text-gray-600']">Group Tuition</h4>
          <div v-if="groupTuitionAvailable" class="text-xl font-bold text-blue-700">
            £{{ Number(silverPrice).toFixed(2) }}<span class="text-xs text-gray-600 font-normal">/week</span>
          </div>
          <div v-else class="text-xl font-bold text-gray-500">N/A</div>
        </div>
        <p :class="['text-xs mb-3', groupTuitionAvailable ? 'text-gray-600' : 'text-gray-500']">{{ groupSubjectText() }}</p>
        <div :class="['flex items-center text-xs mb-1', groupTuitionAvailable ? 'text-gray-700' : 'text-gray-500']">
          <Check :class="['w-4 h-4 mr-2', groupTuitionAvailable ? 'text-blue-600' : 'text-gray-400']" />
          <span>Interactive learning</span>
        </div>
      </div>

      <!-- 1:1 Tuition Plan -->
      <div
        v-if="offersOneOnOne"
        @click="oneOnOneAvailable && !isSubscribed && handleSubscribe('gold')"
        :class="[
          'rounded-xl p-4 border-2 transition-all relative',
          isSubscribedToTier('gold')
            ? 'bg-green-50 border-green-400 cursor-default'
            : isSubscribed
              ? 'bg-gray-100 border-gray-200 opacity-60 cursor-default'
              : oneOnOneAvailable
                ? 'bg-linear-to-br from-yellow-50 to-orange-50 border-yellow-400 hover:border-yellow-600 cursor-pointer'
                : 'bg-gray-200 border-gray-300 opacity-60 cursor-not-allowed',
        ]"
      >
        <div v-if="isSubscribedToTier('gold')" class="absolute -top-2 right-2">
          <span class="bg-green-600 text-white px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1">
            <CheckCircle class="w-3 h-3" />
            SUBSCRIBED
          </span>
        </div>
        <div v-else-if="oneOnOneAvailable" class="absolute -top-2 right-2">
          <span class="bg-linear-to-r from-yellow-500 to-orange-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">PREMIUM</span>
        </div>
        <div class="flex items-center justify-between mb-2">
          <h4 :class="['font-bold', oneOnOneAvailable ? 'text-gray-900' : 'text-gray-600']">1:1 Tuition</h4>
          <div v-if="oneOnOneAvailable" class="text-xl font-bold text-yellow-700">
            £{{ Number(goldPrice).toFixed(2) }}<span class="text-xs text-gray-600 font-normal">/week</span>
          </div>
          <div v-else class="text-xl font-bold text-gray-500">N/A</div>
        </div>
        <p :class="['text-xs mb-3', oneOnOneAvailable ? 'text-gray-600' : 'text-gray-500']">4 personal sessions</p>
        <div :class="['flex items-center text-xs mb-1', oneOnOneAvailable ? 'text-gray-700' : 'text-gray-500']">
          <Check :class="['w-4 h-4 mr-2', oneOnOneAvailable ? 'text-yellow-600' : 'text-gray-400']" />
          <span>Personalized attention</span>
        </div>
      </div>
    </div>

    <button
      v-if="!isSubscribed"
      @click="scrollToPlans"
      class="w-full bg-linear-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all cursor-pointer"
    >
      View All Plans
    </button>
    <button
      v-else
      @click="scrollToPlans"
      class="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all cursor-pointer"
    >
      View All Plans
    </button>

    <div class="mt-4 bg-white/50 backdrop-blur-sm rounded-lg p-3">
      <div class="flex items-center text-xs text-gray-700">
        <Check class="w-4 h-4 text-green-600 mr-2" />
        <span>30-day money-back guarantee</span>
      </div>
    </div>
  </section>
</template>
